import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faSpinner,
  faCubesStacked,
  faPlus,
  faXmark,
  faExpand,
} from '@fortawesome/free-solid-svg-icons';
import {
  equipmentModelService,
  type EquipmentModel,
  type CreateEquipmentModelPayload,
} from '@/services/equipment-model.service';
import { saveEquipmentModel } from './equipment-model-save';
import { accessoryService } from '@/services/accessory.service';
import { serviceTaskService } from '@/services/service-task.service';
import { manufacturerService } from '@/services/manufacturer.service';
import { equipmentTypeService } from '@/services/equipment-type.service';
import { equipmentCategoryService } from '@/services/equipment-category.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;
const MAX_IMAGE_BYTES = 50 * 1024;
const ACCEPTED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// ─── Image helpers ──────────────────────────────────────────────────────────

function validateImageFile(file: File): string | null {
  if (!ACCEPTED_MIME.includes(file.type)) return 'Only JPG, PNG, and WEBP images are accepted';
  return null;
}

async function compressToJpeg(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const compress = (w: number, h: number, quality: number): void => {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas not available'));
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Compression failed'));
              return;
            }
            if (blob.size <= MAX_IMAGE_BYTES) {
              resolve(
                new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }),
              );
            } else if (quality > 0.15) {
              compress(w, h, quality - 0.1);
            } else if (w > 100) {
              compress(Math.round(w * 0.75), Math.round(h * 0.75), 0.8);
            } else {
              resolve(
                new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }),
              );
            }
          },
          'image/jpeg',
          quality,
        );
      };
      compress(img.naturalWidth, img.naturalHeight, 0.9);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

// ─── Lightbox ───────────────────────────────────────────────────────────────

function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85"
      onClick={onClose}
    >
      <img
        src={url}
        className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ─── Accessory multi-select ─────────────────────────────────────────────────

function AccessoryMultiSelect({
  selectedIds,
  onChange,
  disabled,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  const [search, setSearch] = useState('');

  const {
    data: all,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['accessories-all-for-select'],
    queryFn: () => accessoryService.listAll(),
  });

  const filtered = (all ?? []).filter(
    (a) => a.isActive && a.name.toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const selected = (all ?? []).filter((a) => selectedIds.includes(a.id));

  if (isLoading) return <div className="h-10 animate-pulse rounded-lg bg-muted" />;

  if (isError)
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-destructive">Failed to load accessories.</span>
        <button type="button" onClick={() => void refetch()} className="text-primary underline">
          Retry
        </button>
      </div>
    );

  if (!all || all.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        No accessories configured yet — add some in Master Data → Accessories.
      </p>
    );

  return (
    <div className="space-y-2">
      <Input
        placeholder="Search accessories..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={disabled}
      />
      <div className="max-h-36 overflow-y-auto rounded-lg border border-input bg-card p-2 space-y-0.5">
        {filtered.length === 0 ? (
          <p className="px-1 py-1 text-xs text-muted-foreground">No matches</p>
        ) : (
          filtered.map((a) => (
            <label
              key={a.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted/40"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(a.id)}
                onChange={() => toggle(a.id)}
                disabled={disabled}
                className="rounded accent-primary"
              />
              <span className="text-sm text-foreground">{a.name}</span>
            </label>
          ))
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
            >
              {a.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(a.id)}
                  className="hover:text-primary/70"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Service task multi-select ───────────────────────────────────────────────

function ServiceTaskMultiSelect({
  selectedIds,
  onChange,
  disabled,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  const [search, setSearch] = useState('');

  const {
    data: all,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['service-tasks-all-for-select'],
    queryFn: () => serviceTaskService.listAll(),
  });

  const filtered = (all ?? []).filter(
    (t) =>
      t.isActive &&
      (t.code.toLowerCase().includes(search.toLowerCase()) ||
        t.name.toLowerCase().includes(search.toLowerCase())),
  );

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const selected = (all ?? []).filter((t) => selectedIds.includes(t.id));

  if (isLoading) return <div className="h-10 animate-pulse rounded-lg bg-muted" />;

  if (isError)
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-destructive">Failed to load service tasks.</span>
        <button type="button" onClick={() => void refetch()} className="text-primary underline">
          Retry
        </button>
      </div>
    );

  if (!all || all.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        No service tasks configured yet — add some in Master Data → Service Tasks.
      </p>
    );

  return (
    <div className="space-y-2">
      <Input
        placeholder="Search service tasks..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={disabled}
      />
      <div className="max-h-36 overflow-y-auto rounded-lg border border-input bg-card p-2 space-y-0.5">
        {filtered.length === 0 ? (
          <p className="px-1 py-1 text-xs text-muted-foreground">No matches</p>
        ) : (
          filtered.map((t) => (
            <label
              key={t.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted/40"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(t.id)}
                onChange={() => toggle(t.id)}
                disabled={disabled}
                className="rounded accent-primary"
              />
              <span className="text-sm text-foreground">
                <span className="font-mono text-xs text-muted-foreground mr-1">{t.code}</span>
                {t.name}
              </span>
            </label>
          ))
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((t) => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
            >
              {t.code}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(t.id)}
                  className="hover:text-primary/70"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Image upload section ────────────────────────────────────────────────────

interface PendingEntry {
  file: File;
  previewUrl: string;
}

function ImageUploadSection({
  existingUrls,
  pendingEntries,
  removedIndices,
  onAddFiles,
  onRemoveExisting,
  onRemovePending,
  onPreview,
  disabled,
}: {
  existingUrls: string[];
  pendingEntries: PendingEntry[];
  removedIndices: number[];
  onAddFiles: (files: File[]) => void;
  onRemoveExisting: (index: number) => void;
  onRemovePending: (index: number) => void;
  onPreview: (url: string) => void;
  disabled: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const visibleExisting = existingUrls.filter((_, i) => !removedIndices.includes(i));
  const totalCount = visibleExisting.length + pendingEntries.length;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const available = 2 - totalCount;
    if (available <= 0) return;
    const toProcess = files.slice(0, available);
    const results: File[] = [];
    for (const f of toProcess) {
      const err = validateImageFile(f);
      if (err) {
        toast.error(err);
        continue;
      }
      try {
        const compressed = await compressToJpeg(f);
        results.push(compressed);
      } catch {
        toast.error(`Failed to process ${f.name}`);
      }
    }
    if (results.length > 0) onAddFiles(results);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {existingUrls.map((url, i) => {
          if (removedIndices.includes(i)) return null;
          return (
            <div key={`ex-${i}`} className="group relative">
              <img
                src={url}
                className="h-20 w-20 cursor-pointer rounded-lg border border-border object-cover transition-opacity group-hover:opacity-80"
                onClick={() => onPreview(url)}
              />
              <button
                type="button"
                onClick={() => onPreview(url)}
                className="absolute bottom-1 right-1 hidden h-5 w-5 items-center justify-center rounded-full bg-black/50 text-white group-hover:flex"
              >
                <FontAwesomeIcon icon={faExpand} className="h-2.5 w-2.5" />
              </button>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveExisting(i)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}

        {pendingEntries.map((entry, i) => (
          <div key={`pend-${i}`} className="relative">
            <img
              src={entry.previewUrl}
              className="h-20 w-20 rounded-lg border-2 border-dashed border-primary object-cover opacity-80"
            />
            <span className="absolute bottom-0 left-0 right-0 rounded-b-lg bg-primary/80 py-0.5 text-center text-[9px] font-medium text-white">
              NEW
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemovePending(i)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow"
              >
                <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
        ))}

        {totalCount < 2 && !disabled && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary"
          >
            <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
            <span className="text-[10px]">Add</span>
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="sr-only"
        onChange={handleFileChange}
      />
      <p className="text-xs text-muted-foreground">
        JPG, PNG, WEBP — max 2 images, compressed to 50 KB
      </p>
    </div>
  );
}

// ─── Equipment Model Form ────────────────────────────────────────────────────

interface FormInitial {
  name?: string;
  manufacturerId?: string;
  equipmentTypeId?: string;
  modelNumber?: string;
  description?: string;
  accessoryIds?: string[];
  serviceTaskIds?: string[];
  imageUrls?: string[];
}

/** Exported so the inline creation modal can type its `onSubmit` handler
 *  without redeclaring the shape (Phase C.4). */
export interface FormSubmitData {
  payload: CreateEquipmentModelPayload;
  pendingFiles: File[];
  removedImageIndices: number[];
}

/** Exported for reuse by the inline creation modals (Phase C.4) — the intake
 *  workflow must never duplicate this form, including its accessory, service
 *  task and image handling.
 *
 *  `lockRelations` is optional and used only by the inline flow: manufacturer
 *  and equipment type arrive pre-selected via `initial` and are rendered
 *  read-only. The Master Data page omits it, so its behaviour is unchanged. */
export function EquipmentModelForm({
  initial,
  onSubmit,
  onCancel,
  saving,
  lockRelations = false,
}: {
  initial?: FormInitial;
  onSubmit: (data: FormSubmitData) => void;
  onCancel: () => void;
  saving: boolean;
  lockRelations?: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [manufacturerId, setManufacturerId] = useState(initial?.manufacturerId ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [equipmentTypeId, setEquipmentTypeId] = useState(initial?.equipmentTypeId ?? '');
  const [modelNumber, setModelNumber] = useState(initial?.modelNumber ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState<string[]>(
    initial?.accessoryIds ?? [],
  );
  const [selectedServiceTaskIds, setSelectedServiceTaskIds] = useState<string[]>(
    initial?.serviceTaskIds ?? [],
  );
  const [pendingEntries, setPendingEntries] = useState<PendingEntry[]>([]);
  const [removedImageIndices, setRemovedImageIndices] = useState<number[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const pendingEntriesRef = useRef<PendingEntry[]>([]);
  useEffect(() => {
    pendingEntriesRef.current = pendingEntries;
  }, [pendingEntries]);
  useEffect(() => {
    return () => {
      pendingEntriesRef.current.forEach((e) => URL.revokeObjectURL(e.previewUrl));
    };
  }, []);

  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers-all'],
    queryFn: () => manufacturerService.list({ pageSize: 100 }),
  });
  const { data: categories } = useQuery({
    queryKey: ['equipment-categories-all'],
    queryFn: () => equipmentCategoryService.list({ pageSize: 100 }),
  });
  const { data: types } = useQuery({
    queryKey: ['equipment-types-all'],
    queryFn: () => equipmentTypeService.list({ pageSize: 100 }),
  });

  const filteredTypes = categoryId
    ? (types?.items.filter((t) => t.categoryId === categoryId) ?? [])
    : (types?.items ?? []);

  const addFiles = useCallback((files: File[]) => {
    const entries = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingEntries((prev) => [...prev, ...entries]);
  }, []);

  const removePending = useCallback(
    (i: number) => {
      URL.revokeObjectURL(pendingEntries[i].previewUrl);
      setPendingEntries((prev) => prev.filter((_, idx) => idx !== i));
    },
    [pendingEntries],
  );

  const removeExisting = useCallback((i: number) => {
    setRemovedImageIndices((prev) => [...prev, i]);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!modelNumber.trim()) errs.modelNumber = 'Model Number is required';
    if (!manufacturerId) errs.manufacturerId = 'Manufacturer is required';
    if (!equipmentTypeId) errs.equipmentTypeId = 'Equipment type is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({
      payload: {
        modelNumber: modelNumber.trim(),
        manufacturerId,
        equipmentTypeId,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        accessoryIds: selectedAccessoryIds.length > 0 ? selectedAccessoryIds : undefined,
        serviceTaskIds: selectedServiceTaskIds.length > 0 ? selectedServiceTaskIds : undefined,
      },
      pendingFiles: pendingEntries.map((e) => e.file),
      removedImageIndices,
    });
  };

  const selectClass =
    'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

  return (
    <>
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Inline creation from the intake workflow pre-fills manufacturer and
            equipment type and must not offer them for re-selection (C.4). They
            are shown read-only so the operator can see what the model is being
            created under. */}
        {lockRelations ? (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Manufacturer</span>
              <span className="font-medium text-foreground">
                {manufacturers?.items.find((m) => m.id === manufacturerId)?.name ?? '—'}
              </span>
            </div>
            <div className="mt-1 flex justify-between gap-2">
              <span className="text-muted-foreground">Equipment Type</span>
              <span className="font-medium text-foreground">
                {types?.items.find((t) => t.id === equipmentTypeId)?.name ?? '—'}
              </span>
            </div>
          </div>
        ) : (
          <>
            <FormField label="Manufacturer" htmlFor="em-mfr" required error={errors.manufacturerId}>
              <select
                id="em-mfr"
                value={manufacturerId}
                onChange={(e) => setManufacturerId(e.target.value)}
                disabled={saving}
                className={selectClass}
              >
                <option value="">Select a manufacturer...</option>
                {manufacturers?.items.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Category (filter)" htmlFor="em-cat">
              <select
                id="em-cat"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setEquipmentTypeId('');
                }}
                disabled={saving}
                className={selectClass}
              >
                <option value="">All categories</option>
                {categories?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              label="Equipment Type"
              htmlFor="em-type"
              required
              error={errors.equipmentTypeId}
            >
              <select
                id="em-type"
                value={equipmentTypeId}
                onChange={(e) => setEquipmentTypeId(e.target.value)}
                disabled={saving}
                className={selectClass}
              >
                <option value="">Select a type...</option>
                {filteredTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </FormField>
          </>
        )}

        <FormField label="Model Number" htmlFor="em-num" required error={errors.modelNumber}>
          <Input
            id="em-num"
            value={modelNumber}
            onChange={(e) => setModelNumber(e.target.value)}
            placeholder="e.g. GA75"
            disabled={saving}
          />
        </FormField>

        <FormField label="Model Name" htmlFor="em-name">
          <Input
            id="em-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Oil Injected Rotary Screw Compressor"
            disabled={saving}
          />
        </FormField>

        <FormField label="Description" htmlFor="em-desc">
          <Textarea
            id="em-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            disabled={saving}
          />
        </FormField>

        <FormField label="Default Accessories" htmlFor="em-acc">
          <AccessoryMultiSelect
            selectedIds={selectedAccessoryIds}
            onChange={setSelectedAccessoryIds}
            disabled={saving}
          />
        </FormField>

        <FormField label="Default Service Tasks" htmlFor="em-tasks">
          <ServiceTaskMultiSelect
            selectedIds={selectedServiceTaskIds}
            onChange={setSelectedServiceTaskIds}
            disabled={saving}
          />
        </FormField>

        <FormField label="Model Images" htmlFor="em-images">
          <ImageUploadSection
            existingUrls={initial?.imageUrls ?? []}
            pendingEntries={pendingEntries}
            removedIndices={removedImageIndices}
            onAddFiles={addFiles}
            onRemoveExisting={removeExisting}
            onRemovePending={removePending}
            onPreview={setLightboxUrl}
            disabled={saving}
          />
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} className="min-w-[100px]">
            {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
          </Button>
        </div>
      </form>
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function EquipmentModelsPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EquipmentModel | null>(null);
  const [tableLightboxUrl, setTableLightboxUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-models', page, debouncedSearch],
    queryFn: () =>
      equipmentModelService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['equipment-models'] });

  const deleteMutation = useMutation({
    mutationFn: equipmentModelService.remove,
    onSuccess: () => {
      toast.success('Model deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete model'),
  });

  const handleFormSubmit = async ({
    payload,
    pendingFiles,
    removedImageIndices,
  }: FormSubmitData) => {
    setIsSaving(true);
    try {
      await saveEquipmentModel({
        payload,
        pendingFiles,
        removedImageIndices,
        editTargetId: editTarget?.id,
      });
      toast.success(editTarget ? 'Model updated' : 'Model created');
      closeModal();
      invalidate();
    } catch {
      toast.error('Failed to save model');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (m: EquipmentModel) => {
    if (await confirmDelete(m.name ?? m.modelNumber)) deleteMutation.mutate(m.id);
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (m: EquipmentModel) => {
    setEditTarget(m);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      {tableLightboxUrl && (
        <Lightbox url={tableLightboxUrl} onClose={() => setTableLightboxUrl(null)} />
      )}

      <PageHeader
        title="Equipment Models"
        description="Specific equipment models and variants"
        onAdd={can('model:create') ? openCreate : undefined}
        addLabel="Add Model"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search models..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-3 py-3 font-medium text-foreground">Image</th>
                  <th className="px-4 py-3 font-medium text-foreground">Manufacturer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment Type</th>
                  <th className="px-4 py-3 font-medium text-foreground">Model Number</th>
                  <th className="px-4 py-3 font-medium text-foreground">Model Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Accessories</th>
                  <th className="px-4 py-3 font-medium text-foreground">Tasks</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2">
                      {m.imageUrls.length > 0 ? (
                        <img
                          src={m.imageUrls[0]}
                          className="h-10 w-10 cursor-pointer rounded-lg object-cover hover:opacity-80"
                          onClick={() => setTableLightboxUrl(m.imageUrls[0])}
                          alt={m.name ?? m.modelNumber}
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground/40">
                          <FontAwesomeIcon icon={faCubesStacked} className="h-4 w-4" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{m.manufacturerName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.equipmentTypeName}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{m.modelNumber}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.name ?? '—'}</td>
                    <td className="px-4 py-3">
                      {m.accessories.length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {m.accessories.length} item{m.accessories.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {m.serviceTasks.length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {m.serviceTasks.length} task{m.serviceTasks.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          m.isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('model:update') && (
                          <button
                            onClick={() => openEdit(m)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${m.name}`}
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('model:delete') && (
                          <button
                            onClick={() => handleDelete(m)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${m.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faCubesStacked}
          title={search ? 'No models match your search' : 'No equipment models yet'}
          description={search ? 'Try a different search term.' : 'Add your first equipment model.'}
          action={
            !search && can('model:create') ? (
              <Button onClick={openCreate}>Add Model</Button>
            ) : undefined
          }
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Model' : 'Add Equipment Model'}
        size="lg"
      >
        <EquipmentModelForm
          initial={
            editTarget
              ? {
                  name: editTarget.name ?? undefined,
                  manufacturerId: editTarget.manufacturerId,
                  equipmentTypeId: editTarget.equipmentTypeId,
                  modelNumber: editTarget.modelNumber,
                  description: editTarget.description ?? undefined,
                  accessoryIds: editTarget.accessories.map((a) => a.id),
                  serviceTaskIds: editTarget.serviceTasks.map((t) => t.id),
                  imageUrls: editTarget.imageUrls,
                }
              : undefined
          }
          onSubmit={handleFormSubmit}
          onCancel={closeModal}
          saving={isSaving}
        />
      </Modal>
    </div>
  );
}
